import { PriceOverview, GameWithRelations } from "../../types";

/**
 * Форматує ціну з числового значення (в центах) у формат доларів
 * @param priceInCents Ціна в центах
 * @returns Відформатована ціна (наприклад, "$6.39")
 */
export function formatPrice(priceInCents: number): string {
  return `$${(priceInCents / 100).toFixed(2)}`;
}

/**
 * Форматує ціну з priceOverview з fallback
 * @param formatted Відформатована ціна (якщо є)
 * @param numericValue Числове значення ціни в центах (якщо є)
 * @param fallback Fallback значення, якщо немає ні formatted, ні numericValue
 * @returns Відформатована ціна або fallback
 */
export function formatPriceWithFallback(
  formatted: string | undefined | null,
  numericValue: number | undefined | null,
  fallback: string
): string {
  if (formatted) {
    return formatted;
  }

  if (numericValue !== undefined && numericValue !== null) {
    return formatPrice(numericValue);
  }

  return fallback;
}

/**
 * Створює PriceOverview з плоских полів гри, якщо price_overview відсутній
 * @param game Об'єкт гри з полями ціни
 * @returns PriceOverview або null, якщо недостатньо даних
 */
export function createPriceOverview(
  game: GameWithRelations
): PriceOverview | null {
  // Free-to-play не має ціни — нехай PriceRow фолбекне на "Free to Play"
  // з formatGamePrice. Інакше зібрали б priceOverview з price_final=0
  // і відрендерили "$0.00".
  if (game.is_free) {
    return null;
  }

  // Якщо price_overview вже є, повертаємо його
  if (game.price_overview) {
    return game.price_overview;
  }

  // Перевіряємо, чи є достатньо даних для створення priceOverview
  if (
    game.price_final === null ||
    game.price_final === undefined ||
    !game.price_currency
  ) {
    return null;
  }

  const final = game.price_final;
  const initial = game.price_initial ?? final;
  const discount_percent = game.price_discount ?? 0;

  return {
    currency: game.price_currency,
    initial,
    final,
    discount_percent,
    initial_formatted:
      game.price_initial_formatted ?? formatPrice(initial),
    final_formatted: game.price_final_formatted ?? formatPrice(final),
  };
}

/**
 * Формує відформатовану ціну для таблиці метаданих
 * @param game Об'єкт гри
 * @param priceOverview PriceOverview (якщо є)
 * @returns Відформатована ціна або "Free to Play" / "Price Unknown"
 */
export function formatGamePrice(
  game: GameWithRelations,
  priceOverview: PriceOverview | null
): string {
  const isFree = game.is_free ?? false;

  // Якщо гра безкоштовна
  if (isFree) {
    return "Free";
  }

  // Спробуємо отримати відформатовану ціну
  if (game.price_final_formatted) {
    return game.price_final_formatted;
  }

  if (priceOverview?.final_formatted) {
    return priceOverview.final_formatted;
  }

  // Якщо є числове значення, форматуємо його
  if (
    priceOverview?.final !== undefined &&
    priceOverview.final !== null
  ) {
    return formatPrice(priceOverview.final);
  }

  return "Price Unknown";
}
